import { useState, useEffect } from "react";
import { Globe, Database, Cloud, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { adminService } from "@/api/services/admin.service";
import { useAdminSiteSettings } from "@/hooks/api/use-site-settings";
import type { StorageSettings, EmailSettings, S3Config } from "@/api/types";
import SaudiIcon from "@/assets/saudi-icon.svg";
import AmrIcon from "@/assets/amr-icon.svg";

const AdminSettings = () => {
  const { toast } = useToast();
  const { t, language, setLanguage } = useLanguage();
  const {
    settings: siteSettings,
    updateSettings,
    isUpdating,
  } = useAdminSiteSettings();

  const languageOptions = {
    ar: { flag: SaudiIcon, name: 'العربية' },
    en: { flag: AmrIcon, name: 'English' },
  };
  
  // Site settings state
  const [siteName, setSiteName] = useState('');
  const [siteNameAr, setSiteNameAr] = useState('');

  // Update local state when site settings are loaded
  useEffect(() => {
    if (siteSettings) {
      setSiteName(siteSettings.site_name || '');
      setSiteNameAr(siteSettings.site_name_ar || '');
    }
  }, [siteSettings]);

  // Handle site name update
  const handleSaveSiteSettings = async () => {
    updateSettings({
      site_name: siteName,
      site_name_ar: siteNameAr,
    });
  };
  
  // Admin settings state
  const [fileSizeSettings, setFileSizeSettings] = useState<StorageSettings>({
    disk: 'local',
    file_upload_max_size: 10240, // 10MB in KB
    allowed_file_types: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'],
  });

  // File size options in MB (converted to KB for backend)
  const fileSizeOptions = [
    { label: '1 MB', value: 1024 },
    { label: '5 MB', value: 5120 },
    { label: '10 MB', value: 10240 },
    { label: '25 MB', value: 25600 },
    { label: '50 MB', value: 51200 },
    { label: '100 MB', value: 102400 },
    { label: '250 MB', value: 256000 },
    { label: '500 MB', value: 512000 },
    { label: '1 GB', value: 1024000 },
    { label: 'Custom', value: 'custom' },
  ];

  // Common file types for checkboxes
  const commonFileTypes = [
    { value: 'pdf', label: 'PDF' },
    { value: 'doc', label: 'Word Document (.doc)' },
    { value: 'docx', label: 'Word Document (.docx)' },
    { value: 'xls', label: 'Excel Spreadsheet (.xls)' },
    { value: 'xlsx', label: 'Excel Spreadsheet (.xlsx)' },
    { value: 'ppt', label: 'PowerPoint (.ppt)' },
    { value: 'pptx', label: 'PowerPoint (.pptx)' },
    { value: 'jpg', label: 'JPEG Image (.jpg)' },
    { value: 'jpeg', label: 'JPEG Image (.jpeg)' },
    { value: 'png', label: 'PNG Image (.png)' },
    { value: 'gif', label: 'GIF Image (.gif)' },
    { value: 'svg', label: 'SVG Image (.svg)' },
    { value: 'mp4', label: 'MP4 Video (.mp4)' },
    { value: 'avi', label: 'AVI Video (.avi)' },
    { value: 'mov', label: 'MOV Video (.mov)' },
    { value: 'mp3', label: 'MP3 Audio (.mp3)' },
    { value: 'wav', label: 'WAV Audio (.wav)' },
    { value: 'zip', label: 'ZIP Archive (.zip)' },
    { value: 'rar', label: 'RAR Archive (.rar)' },
    { value: '7z', label: '7Z Archive (.7z)' },
    { value: 'txt', label: 'Text File (.txt)' },
    { value: 'csv', label: 'CSV File (.csv)' },
  ];

  // State for custom file size and file type
  const [customFileSize, setCustomFileSize] = useState('');
  const [customFileType, setCustomFileType] = useState('');
  const [showCustomFileSize, setShowCustomFileSize] = useState(false);
  const [showCustomFileType, setShowCustomFileType] = useState(false);

  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    mailer: 'smtp',
    host: 'smtp.gmail.com',
    port: 587,
    username: '',
    password: '',
    encryption: 'tls',
    from_address: '',
    from_name: '',
  });

  const [s3Settings, setS3Settings] = useState<S3Config>({
    access_key_id: '',
    secret_access_key: '',
    region: '',
    bucket: '',
    url: '',
    endpoint: '',
    use_path_style_endpoint: false,
  });

  const [isLoading, setIsLoading] = useState(false);

  // Load current settings on component mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setIsLoading(true);
        
        // Load storage settings
        const storageResponse = await adminService.getStorageSettings();
        if (storageResponse.success && storageResponse.data) {
          // Convert KB to MB for display
          const fileSizeInMB = Math.round(storageResponse.data.file_upload_max_size / 1024);
          const isCustomSize = !fileSizeOptions.find(opt => opt.value === storageResponse.data.file_upload_max_size);
          
          setFileSizeSettings(prev => ({
            ...prev,
            disk: storageResponse.data.disk || 'local',
            file_upload_max_size: storageResponse.data.file_upload_max_size || 10240,
            allowed_file_types: storageResponse.data.allowed_file_types || ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'],
          }));
          
          // Set custom file size if it's not in the predefined options
          if (isCustomSize) {
            setCustomFileSize(fileSizeInMB.toString());
            setShowCustomFileSize(true);
          }
        }
        
        // Load email settings
        const emailResponse = await adminService.getEmailSettings();
        if (emailResponse.success && emailResponse.data) {
          setEmailSettings(prev => ({
            ...prev,
            mailer: emailResponse.data.mailer || 'smtp',
            host: emailResponse.data.host || '',
            port: emailResponse.data.port || 587,
            username: emailResponse.data.username || '',
            password: emailResponse.data.password || '',
            encryption: emailResponse.data.encryption || 'tls',
            from_address: emailResponse.data.from_address || '',
            from_name: emailResponse.data.from_name || '',
          }));
        }
        
        // Load S3 settings if available
        if (storageResponse.success && storageResponse.data?.s3) {
          const s3Data = storageResponse.data.s3;
          setS3Settings(prev => ({
            ...prev,
            access_key_id: s3Data.access_key_id || s3Data.key || '',
            secret_access_key: s3Data.secret_access_key || s3Data.secret || '',
            region: s3Data.region || '',
            bucket: s3Data.bucket || '',
            url: s3Data.url || '',
            endpoint: s3Data.endpoint || '',
            use_path_style_endpoint: s3Data.use_path_style_endpoint || false,
          }));
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
        toast({
          title: t('common.error'),
          description: 'Failed to load current settings',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [t, toast]);

  const handleSaveStorageSettings = async () => {
    try {
      setIsLoading(true);
      
      // Update disk and file_upload_max_size
      const storageUpdateData = {
        disk: fileSizeSettings.disk,
        file_upload_max_size: fileSizeSettings.file_upload_max_size,
      };
      await adminService.updateStorageSettings(storageUpdateData);
      
      // Update allowed file types separately
      await adminService.updateAllowedFileTypes({
        file_types: fileSizeSettings.allowed_file_types,
      });
      
      // Refresh storage settings after successful update
      const storageResponse = await adminService.getStorageSettings();
      if (storageResponse.success && storageResponse.data) {
        setFileSizeSettings(prev => ({
          ...prev,
          disk: storageResponse.data.disk || 'local',
          file_upload_max_size: storageResponse.data.file_upload_max_size || 10240,
          allowed_file_types: storageResponse.data.allowed_file_types || ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'],
        }));
      }
      
      toast({
        title: t('settings.storageUpdated'),
        description: t('settings.storageUpdatedDesc'),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('settings.storageUpdateError'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    try {
      setIsLoading(true);
      await adminService.updateEmailConfig(emailSettings);
      
      // Refresh email settings after successful update
      const emailResponse = await adminService.getEmailSettings();
      if (emailResponse.success && emailResponse.data) {
        setEmailSettings(prev => ({
          ...prev,
          mailer: emailResponse.data.mailer || 'smtp',
          host: emailResponse.data.host || '',
          port: emailResponse.data.port || 587,
          username: emailResponse.data.username || '',
          password: emailResponse.data.password || '',
          encryption: emailResponse.data.encryption || 'tls',
          from_address: emailResponse.data.from_address || '',
          from_name: emailResponse.data.from_name || '',
        }));
      }
      
      toast({
        title: t('settings.emailUpdated'),
        description: t('settings.emailUpdatedDesc'),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('settings.emailUpdateError'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveS3Settings = async () => {
    try {
      setIsLoading(true);
      await adminService.updateS3Config(s3Settings);
      
      // Refresh S3 settings after successful update
      const storageResponse = await adminService.getStorageSettings();
      if (storageResponse.success && storageResponse.data?.s3) {
        const s3Data = storageResponse.data.s3;
        setS3Settings(prev => ({
          ...prev,
          access_key_id: s3Data.access_key_id || s3Data.key || '',
          secret_access_key: s3Data.secret_access_key || s3Data.secret || '',
          region: s3Data.region || '',
          bucket: s3Data.bucket || '',
          url: s3Data.url || '',
          endpoint: s3Data.endpoint || '',
          use_path_style_endpoint: s3Data.use_path_style_endpoint || false,
        }));
      }
      
      toast({
        title: t('settings.s3Updated'),
        description: t('settings.s3UpdatedDesc'),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('settings.s3UpdateError'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t('settings.adminTitle')}</h1>
        <p className="text-muted-foreground">
          {t('settings.adminSubtitle')}
        </p>
      </div>

      {/* Language Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            {t('settings.languageSettings')}
          </CardTitle>
          <CardDescription>
            {t('settings.languageDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-4">
            {Object.entries(languageOptions).map(([code, option]) => (
              <Button
                key={code}
                variant={language === code ? "default" : "outline"}
                onClick={() => setLanguage(code as 'en' | 'ar')}
                className="flex items-center gap-2"
              >
                <img src={option.flag} alt={option.name} className="h-4 w-4" />
                {option.name}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Site Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {t('settings.siteSettings')}
          </CardTitle>
          <CardDescription>
            {t('settings.siteSettingsDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Site Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('settings.siteNameEn')}</Label>
              <Input
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
                placeholder="KYNZ Contracting"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('settings.siteNameAr')}</Label>
              <Input
                value={siteNameAr}
                onChange={(e) => setSiteNameAr(e.target.value)}
                placeholder="كيانز للمقاولات"
                dir="rtl"
              />
            </div>
          </div>

          <Button onClick={handleSaveSiteSettings} disabled={isUpdating}>
            {isUpdating ? t('settings.saving') || 'Saving...' : t('settings.saveSiteSettings')}
          </Button>
        </CardContent>
      </Card>

      {/* Storage Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            {t('settings.storageSettings')}
          </CardTitle>
          <CardDescription>
            {t('settings.storageDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('settings.storageDisk')}</Label>
              <Select
                value={fileSizeSettings.disk}
                onValueChange={(value: 's3' | 'local') => 
                  setFileSizeSettings(prev => ({ ...prev, disk: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="local">{t('settings.serverStorage')}</SelectItem>
                  <SelectItem value="s3">{t('settings.externalStorage')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t('settings.maxFileSize')}</Label>
              <Select
                value={fileSizeOptions.find(opt => opt.value === fileSizeSettings.file_upload_max_size)?.value?.toString() || 'custom'}
                onValueChange={(value: string) => {
                  if (value === 'custom') {
                    setShowCustomFileSize(true);
                  } else {
                    setShowCustomFileSize(false);
                    setFileSizeSettings(prev => ({ 
                      ...prev, 
                      file_upload_max_size: parseInt(value) 
                    }));
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {fileSizeOptions.map((option) => (
                    <SelectItem key={option.value.toString()} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {showCustomFileSize && (
                <Input
                  type="number"
                  placeholder={t('settings.enterCustomSize')}
                  value={customFileSize}
                  onChange={(e) => {
                    const mbValue = parseInt(e.target.value);
                    setCustomFileSize(e.target.value);
                    if (mbValue > 0) {
                      setFileSizeSettings(prev => ({ 
                        ...prev, 
                        file_upload_max_size: mbValue * 1024 // Convert MB to KB
                      }));
                    }
                  }}
                  className="mt-2"
                />
              )}
            </div>
          </div>

          <div className="space-y-4">
            <Label>{t('settings.allowedFileTypes')}</Label>
            
            {/* Checkboxes for common file types */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
              {commonFileTypes.map((type) => (
                <div key={type.value} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`file-type-${type.value}`}
                    checked={fileSizeSettings.allowed_file_types.includes(type.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFileSizeSettings(prev => ({
                          ...prev,
                          allowed_file_types: [...prev.allowed_file_types, type.value]
                        }));
                      } else {
                        setFileSizeSettings(prev => ({
                          ...prev,
                          allowed_file_types: prev.allowed_file_types.filter(t => t !== type.value)
                        }));
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <Label 
                    htmlFor={`file-type-${type.value}`}
                    className="text-sm font-medium text-gray-700 cursor-pointer"
                  >
                    {type.label}
                  </Label>
                </div>
              ))}
            </div>
            
            {/* Custom file type input */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="custom-file-type"
                checked={showCustomFileType}
                onChange={(e) => {
                  setShowCustomFileType(e.target.checked);
                  if (!e.target.checked) {
                    setCustomFileType('');
                  }
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <Label 
                htmlFor="custom-file-type"
                className="text-sm font-medium text-gray-700 cursor-pointer"
              >
                {t('settings.addCustomFileType')}
              </Label>
            </div>
            
            {showCustomFileType && (
              <div className="flex items-center space-x-2 mt-2">
                <Input
                  type="text"
                  placeholder={t('settings.enterFileExtension')}
                  value={customFileType}
                  onChange={(e) => setCustomFileType(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={() => {
                    if (customFileType && !fileSizeSettings.allowed_file_types.includes(customFileType)) {
                      setFileSizeSettings(prev => ({
                        ...prev,
                        allowed_file_types: [...prev.allowed_file_types, customFileType]
                      }));
                      setCustomFileType('');
                    }
                  }}
                  disabled={!customFileType || fileSizeSettings.allowed_file_types.includes(customFileType)}
                >
                  {t('common.add')}
                </Button>
              </div>
            )}
            
            {/* Display current file types */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">
                {t('settings.selectedFileTypes')} ({fileSizeSettings.allowed_file_types.length}):
              </p>
              <div className="flex flex-wrap gap-2">
                {fileSizeSettings.allowed_file_types.map((type) => (
                  <span 
                    key={type}
                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    .{type}
                    <button
                      type="button"
                      onClick={() => {
                        setFileSizeSettings(prev => ({
                          ...prev,
                          allowed_file_types: prev.allowed_file_types.filter(t => t !== type)
                        }));
                      }}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <Button onClick={handleSaveStorageSettings} disabled={isLoading}>
            {isLoading ? t('settings.saving') : t('settings.saveStorageSettings')}
          </Button>
        </CardContent>
      </Card>

      {/* Email Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            {t('settings.emailSettings')}
          </CardTitle>
          <CardDescription>
            {t('settings.emailDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('settings.mailerService')}</Label>
              <Select
                value={emailSettings.mailer}
                onValueChange={(value: 'smtp' | 'pop') => setEmailSettings(prev => ({ ...prev, mailer: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="smtp">SMTP</SelectItem>
                  <SelectItem value="pop">POP</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('settings.host')}</Label>
              <Input
                value={emailSettings.host || ''}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, host: e.target.value }))}
                placeholder="smtp.gmail.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('settings.port')}</Label>
              <Input
                type="number"
                value={emailSettings.port || ''}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, port: parseInt(e.target.value) }))}
                placeholder="587"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('settings.username')}</Label>
              <Input
                value={emailSettings.username || ''}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, username: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('settings.password')}</Label>
              <Input
                type="password"
                value={emailSettings.password || ''}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, password: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('settings.fromAddress')}</Label>
              <Input
                type="email"
                value={emailSettings.from_address || ''}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, from_address: e.target.value }))}
                placeholder="noreply@platform.com"
              />
            </div>
          </div>

          <Button onClick={handleSaveEmailSettings} disabled={isLoading}>
            {isLoading ? t('settings.saving') : t('settings.saveEmailSettings')}
          </Button>
        </CardContent>
      </Card>

      {/* S3 Settings (only show when S3 is selected) */}
      {fileSizeSettings.disk === 's3' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cloud className="h-5 w-5" />
              {t('settings.s3Settings')}
            </CardTitle>
            <CardDescription>
              {t('settings.s3Desc')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('settings.accessKeyId')}</Label>
                <Input
                  value={s3Settings.access_key_id}
                  onChange={(e) => setS3Settings(prev => ({ ...prev, access_key_id: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('settings.secretAccessKey')}</Label>
                <Input
                  type="password"
                  value={s3Settings.secret_access_key}
                  onChange={(e) => setS3Settings(prev => ({ ...prev, secret_access_key: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('settings.region')}</Label>
                <Input
                  value={s3Settings.region}
                  onChange={(e) => setS3Settings(prev => ({ ...prev, region: e.target.value }))}
                  placeholder="us-east-1"
                />
              </div>

              <div className="space-y-2">
                <Label>{t('settings.bucketName')}</Label>
                <Input
                  value={s3Settings.bucket}
                  onChange={(e) => setS3Settings(prev => ({ ...prev, bucket: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('settings.url')} ({t('common.optional')})</Label>
              <Input
                value={s3Settings.url || ''}
                onChange={(e) => setS3Settings(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://s3.amazonaws.com"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('settings.endpoint')} ({t('common.optional')})</Label>
              <Input
                value={s3Settings.endpoint || ''}
                onChange={(e) => setS3Settings(prev => ({ ...prev, endpoint: e.target.value }))}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={s3Settings.use_path_style_endpoint}
                onCheckedChange={(checked) => setS3Settings(prev => ({ ...prev, use_path_style_endpoint: checked }))}
              />
              <Label>{t('settings.usePathStyleEndpoint')}</Label>
            </div>

            <Button onClick={handleSaveS3Settings} disabled={isLoading}>
              {isLoading ? t('settings.saving') : t('settings.saveS3Settings')}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminSettings;
